import React, { useState, forwardRef, useEffect, Fragment, CSSProperties, useMemo, useRef } from "react";
import {
    StyleSheet, View, Text
} from "react-native";
import { useTable, usePagination, useSortBy, useRowSelect } from "react-table";
import { FONT, SESSIONSTORAGE } from "../../../value/staticValues";
import ActionMenu from "./actionMenu";

import { ReactComponent as PaginationSortDownImg } from "../../../img/ico-sorting-arrow-down.svg";

import { ReactComponent as RowDetailImg } from "../../../img/ico-prefix-arrow-right.svg";
import { ReactComponent as SearchNoResultImg } from "../../../img/ico-search-no-result.svg";


import CategoryFilter from "./categoryFilter";
import { ListActionMenu, ListCategoryFilter, ListFilter, ListQueryFilter, ListQueryMenu, ListQueryPeriod, ListTableHeader } from "./listTableType";
import Pagination from "./pagination";
import RowSelectCheckBox from "./rowSelectCheckBox";
import { base64Decoding, base64Encoding, getCurrentTimestamp } from "../../../common/util";
import CategorySubFilter from "./categorySubFilter";
import QueryMenu from "./queryMenu";
import { useTranslation } from "react-i18next";


interface ListTableCellClick{
    id: string;
    clickCb: Function;
}

interface ListTableProps {
    id: string;
    categoryFilters?: ListFilter[];
    categorySubFilters?: ListFilter[];
    actionMenus?: ListActionMenu[];
    actionMenuClickCb?: Function;
    queryMenus?: ListQueryMenu[];
    queryMenuClickCb?: Function;
    searchBarYn: boolean;
    searchBarPlaceholder?: string;
    pageYn: boolean;
    rowCheckYn: boolean;
    rowCheckCb?: Function;
    rowClickCb?: Function;
    fetchDataCb: Function;
    currentPage?: number;
    pageCount: number;
    pageSize: number;
    totalItemCount: number;
    detailYn: boolean;
    tableHeaders: ListTableHeader|any;
    tableData: any;
    refreshYn: boolean;
    canLoadYn: boolean;
    width?: number;
    minHeight?: number;
    cellClickCb?: ListTableCellClick[];
    queryArgs?: object;
}


function ListTable(props:ListTableProps) {
    const { t } = useTranslation();

    const { getTableProps, getTableBodyProps, headerGroups, prepareRow,
        page, canPreviousPage, canNextPage,  pageOptions, pageCount, gotoPage, nextPage, previousPage,  setPageSize, selectedFlatRows,
        setSortBy,
        state: { pageIndex, pageSize, selectedRowIds, sortBy }, } = useTable({
                                    columns: props.tableHeaders,
                                    data:props.tableData,
                                    initialState: { pageIndex: props.id === base64Decoding(sessionStorage.getItem(SESSIONSTORAGE.TABLE_ID) || " ") ? Number(base64Decoding(sessionStorage.getItem(SESSIONSTORAGE.TABLE_PAGE_INDEX) || " ") || 0) : 0, pageSize: props.pageSize},
                                    manualPagination: props.pageYn,
                                    pageCount: props.pageCount,
                                    pageSize: props.pageSize,
                                    manualSortBy: true,
                                    }, useSortBy, usePagination, useRowSelect, hooks => {rowSelectHook(hooks)});

    const [category, setCategory] = useState<ListCategoryFilter>({type: "", code: ""});
    const [search, setSearch] = useState('');
    const [categoryFilters, setCategoryFilters] = useState(props.categoryFilters || []);
    const [categorySubFilters, setCategorySubFilters] = useState(props.categorySubFilters || []);
    const [queryFilters, setQueryFilters] = useState<ListQueryFilter[]>([]);
    const [queryPeriod, setQueryPeriod] = useState<ListQueryPeriod>({type:"", startTs: getCurrentTimestamp() - 31536000, endTs: getCurrentTimestamp()});

    const [actionMenus, setActionMenus] = useState(props.actionMenus || []);
    const [queryMenus, setQueryMenus] = useState(props.queryMenus || []);
    const [searchBarYn, setSearchBarYn] = useState(props.searchBarYn);
    const [queryArgs, setQueryArgs] = useState(props.queryArgs);
    const [hoverRowIndex, setHoverRowIndex] = useState(-1);
    const [initDoneYn, setInitDoneYn] = useState(false);

    function rowSelectHook(hooks:any) {
        if(props.rowCheckYn) {
        hooks.visibleColumns.push(columns => [
            {
              id: 'selection',
              Header: ({ getToggleAllPageRowsSelectedProps }) => (
                <div>
                    <RowSelectCheckBox {...getToggleAllPageRowsSelectedProps()} />
                </div>
              ),
              Cell: ({ row }) => (
                <div>
                    <RowSelectCheckBox {...row.getToggleRowSelectedProps()} />

                </div>
              ),
            },
            ...columns,
            props.detailYn ? {
                id: "detail",
                Header: <div style={{width: "20px"}}></div>,
                Cell: <div style={{width: "20px"}}>
                    <RowDetailImg />
                </div>
            }: {}
          ])
        } else {
            hooks.visibleColumns.push(columns => [
                ...columns,
                props.detailYn ? {
                    id: "detail",
                    Header: <div style={{width: "20px"}}></div>,
                    Cell: <div style={{width: "20px"}}>
                        <RowDetailImg />
                    </div>
                }: {}
              ])
        }
    }

    useEffect(() => {
        if(props.queryArgs) {
            gotoPage(0);
            setQueryArgs(props.queryArgs);
        } else {
            setQueryArgs(undefined);
        }
    }, [props.queryArgs]);


    useEffect(() => {
        if(props.categoryFilters) {
            setCategoryFilters(props.categoryFilters);
        } else {
            setCategoryFilters([]);
        }
    }, [props.categoryFilters]);

    useEffect(() => {
        if(props.categorySubFilters) {
            setCategorySubFilters(props.categorySubFilters);
        } else {
            setCategorySubFilters([]);
        }
    }, [props.categorySubFilters]);

    useEffect(()=>{
        const intervalRefresh = setInterval(()=>{
            if(pageIndex === props.currentPage) {
                executeFetchData(false);
            }
        }, 10 * 1000)

        if(Object.keys(selectedRowIds).length !== 0) {
            clearInterval(intervalRefresh)
        }

        return () => clearInterval(intervalRefresh)
    },[props.currentPage, pageIndex, pageSize, sortBy, category, search, initDoneYn, queryFilters, queryPeriod, props.canLoadYn, queryArgs, selectedRowIds])

    useEffect(() => {
        if(initDoneYn && props.canLoadYn) {
            executeFetchData();
        }

    }, [props.fetchDataCb, pageIndex, pageSize, sortBy, category, search, initDoneYn, queryFilters, queryPeriod, props.canLoadYn, queryArgs]);

    useEffect(() => {
        if(props.refreshYn && initDoneYn && props.canLoadYn) {
            executeFetchData();
        }
    }, [props.refreshYn]);

    useEffect(() => {
        if(props.rowCheckCb) {
            props.rowCheckCb(selectedFlatRows.map((selectedRow) => {
                return selectedRow.original;
            }));
        }
    }, [selectedRowIds, selectedFlatRows]);

    useEffect(() => {
        const idFromLs= base64Decoding(sessionStorage.getItem(SESSIONSTORAGE.TABLE_ID) || " ");
        const pageSizeFromLs = base64Decoding(sessionStorage.getItem(SESSIONSTORAGE.TABLE_PAGE_SIZE) || " ");
        const pageIndexFromLs = base64Decoding(sessionStorage.getItem(SESSIONSTORAGE.TABLE_PAGE_INDEX) || " ");
        const searchFromLs = base64Decoding(sessionStorage.getItem(SESSIONSTORAGE.TABLE_SEARCH) || " ");
        const categoryTypeFromLs = base64Decoding(sessionStorage.getItem(SESSIONSTORAGE.TABLE_CATEGORY_FILTER_TYPE) || " ");
        const categoryValueFromLs = base64Decoding(sessionStorage.getItem(SESSIONSTORAGE.TABLE_CATEGORY_FILTER_VALUE) || " ");
        const sortByTypeFromLs = base64Decoding(sessionStorage.getItem(SESSIONSTORAGE.TABLE_SORTY_BY_TYPE) || " ");
        const sortByDescYnFromLs = base64Decoding(sessionStorage.getItem(SESSIONSTORAGE.TABLE_SORTY_BY_DESC_YN) || " ");

        if(props.id === idFromLs) {
            setPageSize(Number(pageSizeFromLs) || props.pageSize);
            setSearch(searchFromLs || '');
            setCategory({type: categoryTypeFromLs || '', code: categoryValueFromLs || ''});
            setSortBy([{id:sortByTypeFromLs || '', desc: sortByDescYnFromLs === "false"?false:true}]);
        } else {
            sessionStorage.setItem(SESSIONSTORAGE.TABLE_ID, base64Encoding(props.id));
            sessionStorage.setItem(SESSIONSTORAGE.TABLE_PAGE_SIZE, base64Encoding(props.pageSize + ""));
            sessionStorage.setItem(SESSIONSTORAGE.TABLE_PAGE_INDEX, base64Encoding(0 + ""));
            sessionStorage.setItem(SESSIONSTORAGE.TABLE_SEARCH, base64Encoding(""));
            sessionStorage.setItem(SESSIONSTORAGE.TABLE_CATEGORY_FILTER_TYPE, base64Encoding(""));
            sessionStorage.setItem(SESSIONSTORAGE.TABLE_CATEGORY_FILTER_VALUE, base64Encoding(""));
            sessionStorage.setItem(SESSIONSTORAGE.TABLE_SORTY_BY_TYPE, base64Encoding(""));
            sessionStorage.setItem(SESSIONSTORAGE.TABLE_SORTY_BY_DESC_YN, base64Encoding(""));
        }
        setInitDoneYn(true);
    }, []);

    function rowMouseHover(index, hoverYn) {
        if(!hoverYn) {
            setHoverRowIndex(-1);
        } else {
            setHoverRowIndex(index);
        }
    }

    function executeFetchData(showLoading = true) {

        let sortByName = '';
        let sortByDescYn:boolean|undefined = false;
        let categoryType = category.type;
        let categoryCode = category.code;
        if(sortBy.length > 0) {
            sortByName = sortBy[0].id;
            sortByDescYn = sortBy[0].desc;
        }

        sessionStorage.setItem(SESSIONSTORAGE.TABLE_ID, base64Encoding(props.id));
        sessionStorage.setItem(SESSIONSTORAGE.TABLE_PAGE_SIZE, base64Encoding(pageSize + ""));
        sessionStorage.setItem(SESSIONSTORAGE.TABLE_PAGE_INDEX, base64Encoding(pageIndex + ""));
        sessionStorage.setItem(SESSIONSTORAGE.TABLE_SEARCH, base64Encoding(search));
        sessionStorage.setItem(SESSIONSTORAGE.TABLE_CATEGORY_FILTER_TYPE, base64Encoding(categoryType));
        sessionStorage.setItem(SESSIONSTORAGE.TABLE_CATEGORY_FILTER_VALUE, base64Encoding(categoryCode));
        sessionStorage.setItem(SESSIONSTORAGE.TABLE_SORTY_BY_TYPE, base64Encoding(sortByName));
        sessionStorage.setItem(SESSIONSTORAGE.TABLE_SORTY_BY_DESC_YN, base64Encoding(sortByDescYn + ""));
        
        props.fetchDataCb({ pageSize, pageIndex, sortByName, sortByDescYn, categoryType, categoryCode, search, queryFilters, queryPeriod, queryArgs, showLoading });
        
    }


    function clickCategoryItem(type, code) {
        gotoPage(0);
        setCategory({...category, type: type, code: code });
    };

    function executeSearchValue(value) {
        gotoPage(0);
        setSearch(value);
    }

    function clickActionMenuItem(code) {
        if(props.actionMenuClickCb)
            props.actionMenuClickCb(code);
    }

    function clickQueryFilter(type, code) {
        gotoPage(0);
        const targetFilters = [...queryFilters];
        let index = -1;
        for(let filter of targetFilters) {
            if(filter.type === type) {
                index = targetFilters.indexOf(filter);
            }
        }
        if(index >= 0) {
            if(code) {
                targetFilters[index] = {type: type, code: code};
                setQueryFilters(targetFilters);
            } else {
                targetFilters.splice(index, 1);
                setQueryFilters(targetFilters);
            }
        } else {
            if(code) {
                setQueryFilters([...queryFilters, {type:type, code: code}]);
            }
        }
    }

    function clickQueryPeriod(type, startTs, endTs) {
        setQueryPeriod({
            type: type,
            startTs: startTs,
            endTs: endTs
        })
    }


    function clickQueryMenuItem(code) {
        if(props.queryMenuClickCb)
            props.queryMenuClickCb(code);
    }


    function clickTableRow(cell, target) {
        if(cell.column.id !== "selection") {
            if(props.cellClickCb) {
                for(let cellClick of props.cellClickCb) {
                    if(cell.column.id === cellClick.id) {
                        return cellClick.clickCb(target.original);
                    }
                }
            }
            if (props.rowClickCb) {
                props.rowClickCb(target.original);
            }
        }
    }
    return (
        <Fragment>
            <div style={props.width ? {...className.topContainer, width:props.width + "px"} : className.topContainer}>
                {
                    categoryFilters.length > 0 ?
                    <CategoryFilter filters={categoryFilters} clickCb={clickCategoryItem}/> : null
                }
                {
                    categorySubFilters.length > 0 ?
                    <CategorySubFilter filters={categorySubFilters} clickCb={clickCategoryItem}/> : null
                }
                {
                    (actionMenus.length > 0 || props.searchBarYn === true) && queryMenus.length === 0?
                    <ActionMenu menus={actionMenus} searchBarYn={searchBarYn} searchBarPlaceholder={props.searchBarPlaceholder} searchValue={search} searchCb={executeSearchValue} clickCb={clickActionMenuItem} periodClickCb={clickQueryPeriod}/>
                    : (queryMenus.length > 0  || props.searchBarYn === true) && actionMenus.length === 0?
                    <QueryMenu menus={queryMenus} searchBarYn={searchBarYn} searchValue={search} searchCb={executeSearchValue} filterClickCb={clickQueryFilter} periodClickCb={clickQueryPeriod} buttonClickCb={clickQueryMenuItem}/>
                    : null
                }
                {
                    <div style={props.minHeight ? {...className.tableContainer, minHeight: props.minHeight + "px" }: className.tableContainer}>

                        {
                            props.pageYn ?
                            <Pagination pageIndex={pageIndex}
                                        pageSize={pageSize}
                                        page={page}
                                        canPreviousPage={canPreviousPage}
                                        canNextPage={canNextPage}
                                        pageOptions={pageOptions}
                                        pageCount={pageCount}
                                        gotoPage={gotoPage}
                                        nextPage={nextPage}
                                        previousPage={previousPage}
                                        setPageSize={setPageSize}
                                        totalItemCount={props.totalItemCount}
                            /> : null
                        }
                        <table style={className.table} {...getTableProps()}>
                            <thead style={className.tableHeader}>
                                {headerGroups.map((headerGroups) => (
                                    <tr style={className.tableHeaderRow}  {...headerGroups.getHeaderGroupProps()}>
                                        {headerGroups.headers.map((column, index) => {
                                            if(index === 0 && props.rowCheckYn) {
                                                return (<th style={className.tableHeaderCheckBoxColumn} {...column.getHeaderProps()}>
                                                    {column.render("Header")}
                                                </th>)
                                            } else if(index === headerGroups.headers.length - 1) {
                                                return (<th style={className.tableDetailLinkColumn} {...column.getHeaderProps()}>
                                                    {column.render("Header")}

                                                </th>)
                                            } else {
                                                return (<th {...column.getHeaderProps(column.disableSortBy === false ? column.getSortByToggleProps(): {})} style={index === 0 ? {...className.tableHeaderColumn, paddingLeft: 20, ...column.style} : {...className.tableHeaderColumn, ...column.style}} >
                                                    <div style={column.disableSortBy === false ? className.tableHeaderSortColumn: className.tableHeaderNormalColumn}>
                                                        {column.render("Header")}
                                                    {
                                                        column.disableSortBy === false?
                                                            column.isSorted ?
                                                                column.isSortedDesc ?
                                                            <View>
                                                                <PaginationSortDownImg />
                                                            </View>
                                                            : <View>
                                                                <PaginationSortDownImg style={{transform: "rotate(180deg)"}}/>
                                                            </View>
                                                            : null
                                                        : null
                                                    }
                                                    </div>
                                                </th>)
                                            }
                                        })}
                                    </tr>
                                ))}
                            </thead>
                            <tbody style={className.tableBody} {...getTableBodyProps()}>
                                {
                                    page.length > 0 ?
                                        page.map((row, index) => {
                                            prepareRow(row);
                                            return (
                                                <tr style={hoverRowIndex === row.index ? className.tableBodyRowHover : row.index in selectedRowIds ? className.tableBodyRowSelected: className.tableBodyRow} {...row.getRowProps()} onMouseOver={()=>{rowMouseHover(row.index, true);}} onMouseOut={()=>{rowMouseHover(row.index, false);}}>
                                                    {row.cells.map((cell, cellIndex) => {
                                                        return <td style={props.rowCheckYn === false && cellIndex === 0 ? {paddingLeft: 20, ...className.tableBodyCell}: className.tableBodyCell} {...cell.getCellProps()}  onClick={() => {clickTableRow(cell,row);}}>{cell.render("Cell")}</td>
                                                    })}
                                                </tr>
                                            )
                                        })
                                    :
                                        <tr><td colSpan={100}>
                                            <View style={style.searchNoResultContainer}>
                                                <SearchNoResultImg />
                                                <Text style={style.searchNoResultMainText}>{t("SEARCH_NO_RESULT_MAIN_TEXT")}</Text>
                                                <Text style={style.searchNoResultSubText}>{t("SEARCH_NO_RESULT_SUB_TEXT")}</Text>
                                            </View>
                                        </td></tr>
                                }
                            </tbody>
                        </table>

                        {
                            props.pageYn ?
                            <Pagination pageIndex={pageIndex}
                                        pageSize={pageSize}
                                        page={page}
                                        canPreviousPage={canPreviousPage}
                                        canNextPage={canNextPage}
                                        pageOptions={pageOptions}
                                        pageCount={pageCount}
                                        gotoPage={gotoPage}
                                        nextPage={nextPage}
                                        previousPage={previousPage}
                                        setPageSize={setPageSize}
                                        totalItemCount={props.totalItemCount}
                            /> : null
                        }
                    </div>


                }
            </div>
        </Fragment>
    );
}



const style = StyleSheet.create({


    searchNoResultContainer: {
        minHeight: 400,
        paddingTop: 121,
        flexDirection: "column",
        alignItems: "center"
    },
    searchNoResultMainText: {
        marginTop: 27,
        fontFamily: FONT.REGULAR,
        fontSize: 18,
        color: "#4d5367"
    },
    searchNoResultSubText: {
        marginTop: 30,
        fontFamily: FONT.REGULAR,
        fontSize: 12,
        color: "#5c5c5c"
    }
});

const className = {
    topContainer: {
        width: "100%",
    },

    tableContainer: {
        marginTop: "25px",
        minHeight: "600px",
    },
    table: {
        width: "100%",
        border: "solid 1px #e4e7eb",
        borderRadius: "2px",
        backgroundColor: "#fff",
        padding: "0px",
        borderSpacing: "unset",
        marginTop: "17px",
        marginBottom: "17px"

    },
    tableHeader: {
        backgroundColor: "#f5f6fa",
    },
    tableHeaderRow: {

        height: "40px",
    },


    tableHeaderColumn: {
        height: "40px",
        alignItems: "center",
        fontFamily: FONT.MEDIUM,
        fontSize: "12px",
        fontWeight: "500",
        lineHeight: "1",
        color: "#333",
        textAlign: "start",

    },

    tableHeaderCheckBoxColumn: {
        width: "22px"
    },

    tableHeaderNormalColumn: {
        display: "flex",
        alignItems: "center"
    },
    tableHeaderSortColumn: {
        display: "flex",
        alignItems: "center",
        cursor: "pointer"
    },
    tableDetailLinkColumn: {
        width: "30px"
    },
    tableBody: {


    },
    tableBodyRow: {
        height: "40px",
        cursor: "pointer"

    },
    tableBodyRowHover: {
        height: "40px",
        backgroundColor: "#f0f3fa",
        boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.2)",
        cursor: "pointer"
    },
    tableBodyRowSelected: {
        height: "40px",
        backgroundColor: "#f0f3fa",
        cursor: "pointer"
    },
    tableBodyCell: {
        borderTop: "1px solid #e4e7eb",
        cursor: "pointer"
    },

};



export default ListTable;